import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, Plus, Minus } from 'lucide-react';
import { SEOHead } from '../components/SEOHead';
import { hero, ctaSection, processSteps, statistics, testimonials } from '../content/site';
import { useEditable, useEditor, DEFAULT_SECTIONS } from '../contexts/EditorContext';
import { EditableImage } from '../components/EditableImage';
import { InlineText } from '../components/InlineText';
import { useLanguage } from '../contexts/LanguageContext';
import { KeywordStory } from '../components/KeywordStory';
import { LayoutSection, LayoutContainer } from '../components/editor/LayoutSection';
import { useProjects } from '../hooks/useProjects';

/* ─── Palette ─── */
const C = {
  bg: '#F5F2ED',
  card: '#FFFFFF',
  text: '#1A1A1A',
  muted: '#6B6B6B',
  light: '#9A9A9A',
  border: 'rgba(0,0,0,0.07)',
  accent: '#1A1A1A',
};

/* ─── Shared animation presets ─── */
const reveal = {
  initial: { opacity: 0, y: 32 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-60px' },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

const revealSlow = {
  ...reveal,
  transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
};

/* ─── Services data ─── */
const services = [
  {
    label: 'Mobilität',
    title: 'Fahrzeuge, die auffallen.',
    desc: 'Von der Lackschutzfolie bis zum kompletten Flottenbranding — wir realisieren Ihre Fahrzeugprojekte mit Präzision und Premium-Qualität.',
    image: '/images/porsche-gt3-cup-race.webp',
    href: '/mobilitaet',
    features: ['PPF & Lackschutz', 'Flottenbranding', 'Teil- & Vollfolierung', 'Designkonzepte'],
  },
  {
    label: 'Raum & Architektur',
    title: 'Räume, die wirken.',
    desc: 'Wir transformieren Räume in Markenerlebnisse — durch Architekturfolierung, Glasgestaltung und Interior Branding.',
    image: '/images/IMG_5646.webp',
    href: '/architektur-raum',
    features: ['Raumgestaltung', 'Architekturfolierung', 'Glas- & Sichtschutzfolien', 'Interior Branding'],
  },
  {
    label: 'Markenkommunikation',
    title: 'Botschaften, die ankommen.',
    desc: 'Von der Messewand bis zur Fassadenwerbung — wir produzieren und installieren Ihre Markenkommunikation in jeder Größe.',
    image: '/images/IMG_7190.webp',
    href: '/markenkommunikation',
    features: ['Großformatmedien', 'Werbesysteme', 'Event- & Messegrafik', 'POS-Systeme'],
  },
];

/* ─── FAQ data ─── */
const faqs = [
  { q: 'Was kostet eine Fahrzeugfolierung?', a: 'Die Kosten variieren je nach Fahrzeugtyp und Umfang. Eine Teilfolierung beginnt bei ca. 800 €, eine Vollfolierung ab ca. 2.500 €. Wir erstellen Ihnen gerne ein individuelles Angebot.' },
  { q: 'Wie lange dauert ein typisches Projekt?', a: 'Fahrzeugprojekte dauern in der Regel 3–7 Werktage. Raum- und Architekturprojekte planen wir mit 2–8 Wochen. Express-Optionen sind bei Bedarf verfügbar.' },
  { q: 'Arbeiten Sie auch außerhalb von NRW?', a: 'Ja, unsere mobilen Teams sind europaweit im Einsatz. Wir realisieren Projekte in ganz Deutschland und im angrenzenden Ausland.' },
  { q: 'Bieten Sie auch Designleistungen an?', a: 'Absolut. Von der Konzeptentwicklung über Visualisierungen bis zur produktionsreifen Reinzeichnung — alles aus einer Hand.' },
  { q: 'Wie läuft die Zusammenarbeit ab?', a: 'Nach einem kurzen Briefing erstellen wir ein Konzept und Angebot. Nach Freigabe starten wir mit Produktion und termingerechter Montage. Qualitätskontrolle inklusive.' },
];

/* ─── FAQ Accordion Item ─── */
function FAQItem({ q, a, qKey, aKey, qFallback, aFallback, isOpen, onToggle }) {
  return (
    <div style={{ borderBottom: `1px solid ${C.border}` }}>
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between py-6 md:py-7 text-left cursor-pointer group"
        data-testid={`faq-toggle-${q.slice(0, 20).replace(/\s/g, '-')}`}
      >
        {qKey ? (
          <InlineText as="span" cmsKey={qKey} fallback={qFallback} className="text-base md:text-lg font-medium pr-8" style={{ color: C.text }} />
        ) : (
          <span className="text-base md:text-lg font-medium pr-8" style={{ color: C.text }}>{q}</span>
        )}
        <span className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors duration-300"
          style={{ background: isOpen ? C.accent : 'transparent', border: `1.5px solid ${isOpen ? C.accent : C.border}` }}
        >
          {isOpen ? <Minus className="w-3.5 h-3.5" style={{ color: '#fff' }} /> : <Plus className="w-3.5 h-3.5" style={{ color: C.muted }} />}
        </span>
      </button>
      <motion.div initial={false} animate={{ height: isOpen ? 'auto' : 0, opacity: isOpen ? 1 : 0 }} transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }} className="overflow-hidden">
        {aKey ? (
          <InlineText as="p" cmsKey={aKey} fallback={aFallback} className="pb-7 pr-12 text-[15px] leading-relaxed" style={{ color: C.muted }} />
        ) : (
          <p className="pb-7 pr-12 text-[15px] leading-relaxed" style={{ color: C.muted }}>{a}</p>
        )}
      </motion.div>
    </div>
  );
}

/* ─── Parallax Image Wrapper ─── */
function ParallaxImage({ src, alt, className = '', contentKey, aspectClass = 'aspect-[4/3]' }) {
  const { scrollYProgress } = useScroll();
  const y = useTransform(scrollYProgress, [0, 1], [0, -40]);
  return (
    <div className={`overflow-hidden rounded-2xl ${aspectClass} ${className}`}>
      <motion.div style={{ y }} className="w-full h-[115%] -mt-[7.5%]">
        {contentKey ? (
          <EditableImage contentKey={contentKey} fallbackSrc={src} alt={alt} className="w-full h-full" imgClassName="object-cover" />
        ) : (
          <img src={src} alt={alt} className="w-full h-full object-cover" loading="lazy" />
        )}
      </motion.div>
    </div>
  );
}

/* ═══════════════════════════════════════════ */
/*         SECTION RENDER FUNCTIONS            */
/* ═══════════════════════════════════════════ */

function HeroSection({ heroSubline, heroCta1, heroCta2 }) {
  return (
    <LayoutSection id="hero" className="!pt-0 !pb-0">
      <KeywordStory
        subline={heroSubline}
        ctaLabel={heroCta1}
        ctaHref={hero.ctaPrimary.href}
        secondaryLabel={heroCta2}
        secondaryHref={hero.ctaSecondary.href}
      />
    </LayoutSection>
  );
}

function TrustBarSection({ trustBarItems }) {
  return (
    <LayoutSection id="trustbar" style={{ borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}` }}>
      <LayoutContainer id="trustbar">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
          {trustBarItems.map((stat, i) => (
            <motion.div key={`stat-${stat.label || i}`} {...reveal} transition={{ ...reveal.transition, delay: i * 0.08 }} className="text-center">
              <InlineText as="div" cmsKey={`stats.${i}.value`} fallback={stat.value} className="text-4xl md:text-5xl font-semibold tracking-[-0.03em]" style={{ color: C.text }} data-testid={`stat-value-${i}`} />
              <InlineText as="div" cmsKey={`stats.${i}.label`} fallback={stat.label} className="mt-2 text-[13px] font-medium" style={{ color: C.muted }} data-testid={`stat-label-${i}`} />
            </motion.div>
          ))}
        </div>
      </LayoutContainer>
    </LayoutSection>
  );
}

function ServicesSection({ sectionLabel, sectionTitle, serviceItems }) {
  return (
    <section id="services" className="relative">
      {/* Section Header - Contained */}
      <div className="max-w-[1400px] mx-auto px-8 md:px-16 lg:px-20 pt-24 md:pt-44 pb-16 md:pb-32">
        <motion.div {...revealSlow}>
          <InlineText as="p" cmsKey="services.sectionLabel" fallback="Leistungen" className="text-[11px] font-semibold uppercase tracking-[0.2em] mb-5" style={{ color: C.light }} />
          <InlineText as="h2" cmsKey="services.sectionTitle" fallback="Was wir tun." className="text-4xl md:text-5xl lg:text-7xl font-semibold tracking-[-0.03em] leading-[1.05]" />
        </motion.div>
      </div>

      {/* Full-Width Service Blocks */}
      {serviceItems.map((svc, i) => (
        <motion.article 
          key={svc.href || `svc-${i}`} 
          {...reveal} 
          transition={{ ...reveal.transition, delay: 0.1 }}
          className="relative w-full"
        >
          {/* ─── MOBILE: Stacked layout (image + text below) ─── */}
          <div className="md:hidden">
            {/* Image – standalone on mobile */}
            <div className="relative w-full aspect-[16/10] overflow-hidden">
              <EditableImage 
                contentKey={`services.${i}.image`} 
                fallbackSrc={svc.image} 
                alt={svc.title} 
                className="absolute inset-0 w-full h-full" 
                imgClassName="object-cover"
              />
              {/* Subtle gradient for title readability */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A] via-black/20 to-transparent" />
              {/* Title floats at the bottom of image */}
              <div className="absolute bottom-0 left-0 right-0 px-6 pb-5">
                <p className="text-[10px] font-semibold uppercase tracking-[0.3em] mb-2 text-white/50">
                  {svc.label}
                </p>
                <h3 className="text-2xl font-semibold tracking-[-0.02em] leading-[1.15] text-white">
                  {svc.title}
                </h3>
              </div>
            </div>
            {/* Text content – separated below the image */}
            <div className="bg-[#1A1A1A] px-6 py-7">
              <p className="text-[13px] leading-[1.75] mb-5 text-white/70 max-w-[340px]">
                {svc.desc}
              </p>
              <ul className="space-y-2 mb-6">
                {svc.features.map((feat, fi) => (
                  <li key={feat} className="flex items-center gap-2.5">
                    <span className="w-1 h-1 rounded-full bg-white/40 flex-shrink-0" />
                    <span className="text-[12px] leading-[1.5] text-white/55">{feat}</span>
                  </li>
                ))}
              </ul>
              <Link 
                to={svc.href} 
                className="inline-flex items-center gap-2.5 text-[12px] font-medium tracking-wide text-white/90 hover:text-white transition-colors duration-300"
              >
                Mehr erfahren <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>

          {/* ─── DESKTOP: Original overlay layout ─── */}
          <div className="hidden md:block relative w-full aspect-[21/9] lg:aspect-[21/8] overflow-hidden">
            <EditableImage 
              contentKey={`services.${i}.image`} 
              fallbackSrc={svc.image} 
              alt={svc.title} 
              className="absolute inset-0 w-full h-full" 
              imgClassName="object-cover"
            />
            <div 
              className={`absolute inset-0 ${
                i % 2 === 0 
                  ? 'bg-gradient-to-r from-black/80 via-black/50 to-black/10' 
                  : 'bg-gradient-to-l from-black/80 via-black/50 to-black/10'
              }`} 
            />
            <div className="absolute inset-0 flex items-center">
              <div className="w-full max-w-[1600px] mx-auto px-16 lg:px-24">
                <div className={`w-full max-w-lg lg:max-w-xl ${i % 2 === 0 ? '' : 'ml-auto text-right'}`}>
                  <InlineText as="p" cmsKey={`services.${i}.label`} fallback={svc.label} className="text-[11px] font-semibold uppercase tracking-[0.3em] mb-4 text-white/50" />
                  <InlineText as="h3" cmsKey={`services.${i}.title`} fallback={svc.title} className="text-4xl lg:text-5xl xl:text-[3.5rem] font-semibold tracking-[-0.03em] leading-[1.1] mb-6 text-white" />
                  <InlineText as="p" cmsKey={`services.${i}.desc`} fallback={svc.desc} className="text-[15px] leading-[1.7] mb-8 text-white/75 max-w-md" />
                  <ul className={`space-y-2 mb-10 ${i % 2 === 0 ? '' : 'flex flex-col items-end'}`}>
                    {svc.features.map((feat, fi) => (
                      <li key={feat} className={`flex items-center gap-2.5 ${i % 2 === 0 ? '' : 'flex-row-reverse'}`}>
                        <span className="w-1 h-1 rounded-full bg-white/40" />
                        <span className="text-[13px] text-white/60">{feat}</span>
                      </li>
                    ))}
                  </ul>
                  <Link 
                    to={svc.href} 
                    className={`inline-flex items-center gap-2.5 text-[13px] font-medium tracking-wide text-white/90 hover:text-white transition-colors duration-300 ${i % 2 === 0 ? '' : 'flex-row-reverse'}`}
                  >
                    {i % 2 === 0 ? (
                      <>Mehr erfahren <ArrowRight className="w-4 h-4" /></>
                    ) : (
                      <><ArrowRight className="w-4 h-4 rotate-180" /> Mehr erfahren</>
                    )}
                  </Link>
                </div>
              </div>
            </div>
          </div>
          
          {/* Spacer between sections */}
          {i < serviceItems.length - 1 && (
            <div className="h-6 md:h-20 lg:h-28" style={{ background: C.bg }} />
          )}
        </motion.article>
      ))}
    </section>
  );
}

function ShowcaseSection({ e }) {
  return (
    <LayoutSection id="showcase">
      <div className="max-w-[1600px] mx-auto px-4 md:px-8">
        <motion.div {...revealSlow}>
          <div className="relative aspect-[21/9] rounded-3xl overflow-hidden shadow-[0_40px_80px_rgba(0,0,0,0.06)]">
            <EditableImage contentKey="images.hero.src" fallbackSrc="/images/Header_Porsche_HD.webp" alt="VISUWORKS Premium Qualität" className="w-full h-full" imgClassName="object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-transparent to-transparent" />
            <div className="absolute inset-0 flex items-end p-8 md:p-14">
              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.3, duration: 0.7 }}>
                <InlineText as="p" cmsKey="homepage.showcase.label" fallback="Premium Qualität" className="text-white/70 text-[12px] font-semibold uppercase tracking-[0.15em] mb-2" />
                <InlineText as="h3" cmsKey="homepage.showcase.title" fallback="Kompromisslos in jedem Detail." className="text-2xl md:text-4xl font-semibold text-white tracking-[-0.02em]" />
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </LayoutSection>
  );
}

function ProjectsSection({ featuredProjects }) {
  return (
    <LayoutSection id="projects">
      <LayoutContainer id="projects">
        <motion.div {...revealSlow} className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16 md:mb-20">
          <div>
            <InlineText as="p" cmsKey="homepage.projects.label" fallback="Portfolio" className="text-[12px] font-semibold uppercase tracking-[0.15em] mb-4" style={{ color: C.light }} />
            <InlineText as="h2" cmsKey="homepage.projects.title" fallback="Ausgewählte Projekte" className="text-3xl md:text-5xl lg:text-6xl font-semibold tracking-[-0.03em]" />
          </div>
          <Link to="/projekte" className="inline-flex items-center gap-2 text-[14px] font-medium group" style={{ color: C.text }}>
            <InlineText as="span" cmsKey="homepage.projects.linkText" fallback="Alle Projekte" /> <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
          </Link>
        </motion.div>
        <div className="grid sm:grid-cols-2 gap-5">
          {featuredProjects.map((project, index) => (
            <motion.div key={project.id || project.slug} {...reveal} transition={{ ...reveal.transition, delay: index * 0.08 }}>
              <Link to={`/projekte/${project.slug}`}>
                <div data-testid={`project-tile-${project.slug}`} className="group relative aspect-[4/3] rounded-2xl overflow-hidden cursor-pointer" style={{ background: '#E8E4DD' }}>
                  {project.coverImage?.url && (
                    <img src={project.coverImage.url} alt={project.coverImage.alt || project.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-transparent" />
                  <div className="absolute inset-0 flex flex-col justify-end p-5 sm:p-6 md:p-8">
                    <span className="text-[10px] sm:text-[11px] font-semibold text-white/60 uppercase tracking-[0.12em] mb-1.5 sm:mb-2 block">{project.category}</span>
                    <h3 className="text-base sm:text-lg md:text-xl font-semibold text-white tracking-[-0.01em] leading-snug">{project.title}</h3>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </LayoutContainer>
    </LayoutSection>
  );
}

function ProcessSection({ processItems, e }) {
  return (
    <LayoutSection id="process" style={{ borderTop: `1px solid ${C.border}` }}>
      <LayoutContainer id="process">
        <motion.div {...revealSlow} className="mb-16 md:mb-24">
          <InlineText as="p" cmsKey="homepage.process.label" fallback="Ablauf" className="text-[12px] font-semibold uppercase tracking-[0.15em] mb-4" style={{ color: C.light }} />
          <InlineText as="h2" cmsKey="homepage.process.title" fallback="Unser Prozess" className="text-3xl md:text-5xl lg:text-6xl font-semibold tracking-[-0.03em]" />
        </motion.div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6 md:gap-8">
          {processItems.map((step, index) => (
            <motion.div key={step.num} {...reveal} transition={{ ...reveal.transition, delay: index * 0.08 }}>
              <div className="h-full p-6 rounded-2xl transition-colors duration-300 hover:bg-black/[0.02]">
                <span className="text-5xl font-semibold" style={{ color: 'rgba(0,0,0,0.06)' }}>{step.num}</span>
                <InlineText as="h3" cmsKey={`process.${index}.title`} fallback={step.title} className="text-base font-semibold mt-5 mb-2" style={{ color: C.text }} />
                <InlineText as="p" cmsKey={`process.${index}.desc`} fallback={step.desc} className="text-[14px] leading-relaxed" style={{ color: C.muted }} />
              </div>
            </motion.div>
          ))}
        </div>
      </LayoutContainer>
    </LayoutSection>
  );
}

function TestimonialsSection({ testimonialItems, e }) {
  return (
    <LayoutSection id="testimonials" style={{ background: '#EFECE6' }}>
      <LayoutContainer id="testimonials">
        <motion.div {...revealSlow} className="mb-16 md:mb-20">
          <InlineText as="p" cmsKey="homepage.testimonials.label" fallback="Kundenstimmen" className="text-[12px] font-semibold uppercase tracking-[0.15em] mb-4" style={{ color: C.light }} />
          <InlineText as="h2" cmsKey="homepage.testimonials.title" fallback="Was unsere Kunden sagen" className="text-3xl md:text-5xl lg:text-6xl font-semibold tracking-[-0.03em]" />
        </motion.div>
        <div className="grid md:grid-cols-2 gap-6">
          {testimonialItems.map((t, i) => (
            <motion.div key={`testimonial-${t.author || i}`} {...reveal} transition={{ ...reveal.transition, delay: i * 0.1 }}>
              <div className="h-full p-8 md:p-10 rounded-2xl" style={{ background: C.card, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                <blockquote className="text-base md:text-lg leading-relaxed mb-8" style={{ color: C.text }}>
                  &bdquo;<InlineText as="span" cmsKey={`testimonials.${i}.quote`} fallback={t.quote} />&ldquo;
                </blockquote>
                <div>
                  <InlineText as="p" cmsKey={`testimonials.${i}.author`} fallback={t.author} className="text-[15px] font-semibold" style={{ color: C.text }} />
                  <p className="text-[13px] mt-0.5" style={{ color: C.muted }}>
                    <InlineText as="span" cmsKey={`testimonials.${i}.position`} fallback={t.position} />, <InlineText as="span" cmsKey={`testimonials.${i}.company`} fallback={t.company} />
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </LayoutContainer>
    </LayoutSection>
  );
}

function FAQSection({ e }) {
  const [openFaq, setOpenFaq] = useState(null);
  return (
    <LayoutSection id="faq">
      <LayoutContainer id="faq">
        <motion.div {...revealSlow} className="text-center mb-16 md:mb-20">
          <InlineText as="p" cmsKey="homepage.faq.label" fallback="FAQ" className="text-[12px] font-semibold uppercase tracking-[0.15em] mb-4" style={{ color: C.light }} />
          <InlineText as="h2" cmsKey="homepage.faq.title" fallback="Häufige Fragen" className="text-3xl md:text-5xl font-semibold tracking-[-0.03em]" />
        </motion.div>
        <motion.div {...reveal}>
          <div style={{ borderTop: `1px solid ${C.border}` }}>
            {faqs.map((faq, i) => (
              <FAQItem key={`faq-${faq.q.substring(0, 20)}`} q={e(`homepage.faq.${i}.q`, faq.q)} a={e(`homepage.faq.${i}.a`, faq.a)} qKey={`homepage.faq.${i}.q`} aKey={`homepage.faq.${i}.a`} qFallback={faq.q} aFallback={faq.a} isOpen={openFaq === i} onToggle={() => setOpenFaq(openFaq === i ? null : i)} />
            ))}
          </div>
        </motion.div>
      </LayoutContainer>
    </LayoutSection>
  );
}

function CTASection({ ctaHeadline, ctaSubline }) {
  return (
    <LayoutSection id="cta">
      <LayoutContainer id="cta">
        <motion.div {...revealSlow}>
          <div className="relative overflow-hidden rounded-3xl p-12 md:p-20 lg:p-28 text-center" style={{ background: C.accent }}>
            <InlineText as="h2" cmsKey="cta.headline" fallback={ctaHeadline} className="text-3xl md:text-5xl lg:text-6xl font-semibold tracking-[-0.03em] leading-[1.1] text-white mb-6" />
            <InlineText as="p" cmsKey="cta.subline" fallback={ctaSubline} className="text-base md:text-lg text-white/60 mb-10 max-w-lg mx-auto leading-relaxed" />
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/kontakt">
                <button data-testid="cta-btn-0" className="inline-flex items-center gap-2 px-8 py-3.5 text-[14px] font-medium rounded-full transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]" style={{ background: '#fff', color: C.accent }}>
                  Kontakt aufnehmen <ArrowRight className="w-4 h-4" />
                </button>
              </Link>
            </div>
          </div>
        </motion.div>
      </LayoutContainer>
    </LayoutSection>
  );
}

/* ═══════════════════════════════════════════ */
/*                 HOME PAGE                   */
/* ═══════════════════════════════════════════ */
export default function HomePage() {
  const { projects: allProjects } = useProjects();
  const featuredProjects = useMemo(() => allProjects.filter(p => p.featured).slice(0, 4), [allProjects]);
  const { getValue, getSectionSettings } = useEditor();
  const e = getValue || ((_, fb) => fb);

  const ctaHeadline = useEditable('cta.headline', ctaSection.headline);
  const ctaSubline = useEditable('cta.subline', ctaSection.subline);

  const trustBarRaw = getValue('trustbar.items', null);
  const trustBarItems = trustBarRaw && Array.isArray(trustBarRaw)
    ? trustBarRaw
    : statistics.map((s, i) => ({
        value: getValue(`stats.${i}.value`, `${s.value}${s.suffix || ''}`),
        label: getValue(`stats.${i}.label`, s.label),
      }));

  const processItems = processSteps.map((step, i) => ({
    ...step,
    title: getValue(`process.${i}.title`, step.title),
    desc: getValue(`process.${i}.desc`, step.desc),
  }));

  const testimonialItems = testimonials.slice(0, 4).map((t, i) => ({
    ...t,
    quote: getValue(`testimonials.${i}.quote`, t.quote),
    author: getValue(`testimonials.${i}.author`, t.author),
    position: getValue(`testimonials.${i}.position`, t.position),
    company: getValue(`testimonials.${i}.company`, t.company),
  }));

  const heroSubline = getValue('hero.subline', hero.subline);
  const heroCta1 = getValue('hero.ctaPrimary.label', hero.ctaPrimary.label);
  const heroCta2 = getValue('hero.ctaSecondary.label', hero.ctaSecondary.label);

  /* ── Services (Was wir tun) CMS binding ── */
  const serviceSectionLabel = getValue('services.sectionLabel', 'Leistungen');
  const serviceSectionTitle = getValue('services.sectionTitle', 'Was wir tun.');
  const serviceItems = services.map((svc, i) => ({
    label: getValue(`services.${i}.label`, svc.label),
    title: getValue(`services.${i}.title`, svc.title),
    desc: getValue(`services.${i}.desc`, svc.desc),
    features: getValue(`services.${i}.features`, svc.features.join(', ')).split(',').map(s => s.trim()).filter(Boolean),
    href: getValue(`services.${i}.href`, svc.href),
    image: svc.image,
  }));

  /* ── Section rendering map ── */
  const sectionMap = {
    hero: <HeroSection heroSubline={heroSubline} heroCta1={heroCta1} heroCta2={heroCta2} />,
    trustbar: <TrustBarSection trustBarItems={trustBarItems} />,
    services: <ServicesSection sectionLabel={serviceSectionLabel} sectionTitle={serviceSectionTitle} serviceItems={serviceItems} />,
    showcase: <ShowcaseSection e={e} />,
    projects: <ProjectsSection featuredProjects={featuredProjects} />,
    process: <ProcessSection processItems={processItems} e={e} />,
    testimonials: <TestimonialsSection testimonialItems={testimonialItems} e={e} />,
    faq: <FAQSection e={e} />,
    cta: <CTASection ctaHeadline={ctaHeadline} ctaSubline={ctaSubline} />,
  };

  /* ── Sort sections by order ── */
  const orderedSections = useMemo(() => {
    return DEFAULT_SECTIONS.map(s => ({
      id: s.id,
      order: getSectionSettings?.(s.id)?.order ?? DEFAULT_SECTIONS.findIndex(d => d.id === s.id),
    })).sort((a, b) => a.order - b.order);
  }, [getSectionSettings]);

  return (
    <div data-testid="home-page" style={{ background: C.bg, color: C.text }}>
      <SEOHead page="home" />
      {orderedSections.map(({ id }) => (
        <React.Fragment key={id}>{sectionMap[id]}</React.Fragment>
      ))}
    </div>
  );
}
